import React, { useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { TextArea, Select, Popup, Input, Button, BUTTON_TYPES } from '_atoms';
import { Typography } from '_atoms/Typography';
import { JOB_SUBMIT_OPTIONS } from 'constants/job';
import { BUTTON_TEXT } from 'constants/text';
import { EXPERIENCE_LEVEL_OPTIONS } from '_organisms/InterviewFeedback/constants';
import { useDecisionOptions, useRejectionOptions, dataMapper } from './utils';
import './style.scss';

import type { DecisionOption } from './utils';

interface SubmitDecisionUserData {
  full_name_latin?: string;
  last_name_latin?: string;
  first_name_latin?: string;
  full_name_local?: string;
  last_name_local?: string;
  first_name_local?: string;
  speciality?: string | null;
}

interface PopupSubmitDecisionProps {
  open: boolean;
  onClose: () => void;
  onSave: (isFinalDecision: boolean, data: ReturnType<typeof dataMapper>) => void;
  userData: SubmitDecisionUserData;
  activeStep: number;
  isFinalDecision: boolean;
  candidateLink?: string;
}

export const PopupSubmitDecision: React.FC<PopupSubmitDecisionProps> = ({
  open,
  onClose,
  onSave,
  userData,
  activeStep,
  isFinalDecision,
  candidateLink,
}) => {
  const { t } = useTranslation();
  const [level, setLevel] = useState<DecisionOption | null>(null);
  const [customLevel, setCustomLevel] = useState('');
  const [period, setPeriod] = useState('');
  const [decision, setDecision] = useState<DecisionOption | null>(null);
  const [rejection, setRejection] = useState<DecisionOption>({ value: 'Other', label: 'Other' });
  const [comment, setComment] = useState('');
  const isManagementApproval = decision?.value === JOB_SUBMIT_OPTIONS.APPROVE && activeStep === 4;

  const onCloseClick = () => {
    setDecision(null);
    setComment('');
    setLevel(null);
    setCustomLevel('');
    setPeriod('');
    setRejection({ value: 'Other', label: 'Other' });
    onClose();
  };

  const onChangeProbationPeriod = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPeriod(e.target.value.replace(/[^0-9]/g, '').slice(-1));

  const onSubmit = () => {
    let reason = '';
    if (decision?.value !== JOB_SUBMIT_OPTIONS.APPROVE) reason = rejection.value;
    onSave(
      isFinalDecision,
      dataMapper(isManagementApproval, reason, comment, decision?.value, period, level?.label, customLevel)
    );
    onCloseClick();
  };

  const decisionOptions = useDecisionOptions(activeStep);
  const rejectionOptions = useRejectionOptions(activeStep, decision);

  useEffect(() => {
    if (decision?.value) {
      setRejection(rejectionOptions?.[0] as DecisionOption);
    }
  }, [decision]);

  return (
    <Popup
      open={open}
      title={
        isFinalDecision
          ? t('translation:popup_submit_decision.title_final_decision')
          : t('translation:popup_submit_decision.title_submit_decision')
      }
      cancelButtonProps={{
        onClick: onCloseClick,
        children: BUTTON_TEXT.CANCEL,
      }}
      okButtonProps={{
        children: BUTTON_TEXT.SUBMIT,
        onClick: onSubmit,
        disabled: isManagementApproval ? !decision || !level || !period : !decision,
      }}
      className="decision-popup mobile-full-size"
    >
      <div className="user-info">
        <Typography.Text className="user-info__title">
          {userData.full_name_latin || `${userData.first_name_latin} ${userData.last_name_latin}`}
        </Typography.Text>
        <Typography.Caption className="user-info__subtitle">
          {userData.full_name_local || `${userData.first_name_local} ${userData.last_name_local}`}
        </Typography.Caption>
        <Typography.Caption className="user-info__subtitle">{userData.speciality}</Typography.Caption>
        {candidateLink && (
          <a target="_blank" href={candidateLink} rel="noreferrer">
            <Button type={BUTTON_TYPES.TEXT}>{t('translation:popup_submit_decision.button_view_candidate')}</Button>
          </a>
        )}
      </div>
      <Select
        label={t('translation:popup_submit_decision.label_decision')}
        value={decision}
        placeholder={t('translation:popup_submit_decision.ph_select_decision')}
        options={decisionOptions}
        onChange={setDecision}
        className="margin-bottom-20"
      />
      {decision && decision.value !== JOB_SUBMIT_OPTIONS.APPROVE && (
        <Select
          label={t('translation:popup_submit_decision.label_rejection_reason')}
          placeholder={t('translation:popup_submit_decision.ph_select_rejection_reason')}
          options={rejectionOptions}
          value={rejection}
          onChange={setRejection}
        />
      )}
      {isManagementApproval && (
        <>
          <Row gutter={12} align="bottom">
            <Col span={12}>
              <Input
                label={t('translation:popup_submit_decision.label_probation_period')}
                placeholder={t('translation:popup_submit_decision.ph_number_of_months')}
                value={period}
                onChange={onChangeProbationPeriod}
              />
            </Col>
            <Col span={12}>
              <Typography.Text className="probation-period-text">
                {t('translation:popup_submit_decision.text_months')}
              </Typography.Text>
            </Col>
          </Row>
          <Row gutter={12} align="bottom">
            <Col span={level?.id === 6 ? 12 : 24}>
              <Select
                label={t('translation:popup_submit_decision.label_assumed_experience_level')}
                options={EXPERIENCE_LEVEL_OPTIONS}
                placeholder={t('translation:popup_submit_decision.ph_select_assumed_technical_level')}
                value={level}
                onChange={(item: DecisionOption) => {
                  setLevel(item);
                  setCustomLevel('');
                }}
              />
            </Col>
            {level?.id === 6 && (
              <Col span={12}>
                <Input
                  placeholder={t('translation:popup_submit_decision.ph_level')}
                  value={customLevel}
                  onChange={e => setCustomLevel(e.target.value)}
                  maxLength={20}
                />
              </Col>
            )}
          </Row>
        </>
      )}
      <TextArea
        label={t('translation:popup_submit_decision.label_comment')}
        value={comment}
        maxLength={6000}
        placeholder={t('translation:popup_submit_decision.ph_enter_comment')}
        onChange={e => setComment(e.target.value)}
      />
    </Popup>
  );
};
